import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, setToken, setRefreshToken, getToken, setOnAuthError, revokeSession } from '../api/client.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const logout = useCallback(() => {
    revokeSession();
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  // Register auth error handler (401s)
  useEffect(() => {
    setOnAuthError(logout);
  }, [logout]);

  // Check for OAuth token in URL hash or query params on mount
  useEffect(() => {
    // Check URL hash for Google OAuth redirect: /#token=...
    const hash = window.location.hash;
    if (hash.startsWith('#token=')) {
      const token = hash.slice(7);
      setToken(token);
      // Clean up URL
      window.history.replaceState(null, '', window.location.pathname);
    }

    // Check query params for auth errors: /?auth_error=...
    const params = new URLSearchParams(window.location.search);
    const error = params.get('auth_error');
    if (error) {
      setAuthError(error);
      window.history.replaceState(null, '', window.location.pathname);
    }

    // Validate existing token
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    auth.me()
      .then(userData => {
        setUser(userData);
      })
      .catch(() => {
        setToken(null);
        setRefreshToken(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await auth.login(email, password);
    setToken(data.token);
    if (data.refreshToken) {
      setRefreshToken(data.refreshToken);
    }
    setUser(data.user);
    return data;
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    authError,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
